import type { Error } from '@/views/apps/_customPage/types'
import { type ExtendedBlock, type ListView } from 'widget-components'
import type { Ref } from 'vue'
import { path } from 'ramda'
import { useI18n } from '@/composables/i18n'
import { updateBlock } from 'widget-components/integrations/CustomPage/blocks-state'

const translateErrorCode = (errorCode: string, block: ExtendedBlock) => {
    const { t } = useI18n()
    return t(`error.${errorCode}`, {
        value: path(['currentValue'], block),
        min: path(['min'], block),
        max: path(['max'], block),
        length: path(['length'], block),
    })
}

const isListView = (block: ExtendedBlock): block is ExtendedBlock<ListView> => block.type === 'LIST_VIEW'

export const handleComponentErrors = (flattenedBlocks: Ref<ExtendedBlock[]>, errors: Error[]): boolean => {
    const isCp2 = flattenedBlocks.value[0].type === 'CONTAINER'
    let handled = false

    if (isCp2) {
        // Group errors by componentIndex to handle multiple errors per component
        const errorsByComponent = new Map<string, Set<string>>()

        errors.forEach(error => {
            const block = flattenedBlocks.value.find(block => block.id === error.componentIndex)
            if (!block) {
                return
            }

            handled = true

            const errorMessage = error.message || translateErrorCode(error.code, block)
            const existingErrors = errorsByComponent.get(error.componentIndex) || new Set<string>()
            existingErrors.add(errorMessage)
            errorsByComponent.set(error.componentIndex, existingErrors)
        })

        // Update blocks with combined unique errors
        errorsByComponent.forEach((errorMessages, componentIndex) => {
            const block = flattenedBlocks.value.find(block => block.id === componentIndex)
            if (block) {
                updateBlock({
                    ...block,
                    error: Array.from(errorMessages).join(', '),
                })
            }
        })

        return handled
    }

    // Group errors by componentIndex for CP1
    const errorsByComponent = new Map<string, Set<string>>()
    const listViewErrors = new Map<string, Set<string>>()

    errors.forEach(error => {
        const [blockId, listViewIndex, listViewComponentId] = (error.componentIndex ?? '').split('.')

        const blockIndex = flattenedBlocks.value.findIndex(block => block.id === blockId)
        const block = flattenedBlocks.value[blockIndex]

        if (!block) {
            return
        }

        handled = true

        if (isListView(block) && listViewIndex) {
            const itemIndex = block.items.findIndex(item => String(item.index) === listViewIndex)
            const item = block.items[itemIndex]
            const component = item?.components.find(component => component.id === listViewComponentId)

            if (component) {
                const errorMessage = error.message || translateErrorCode(error.code, component)
                const key = `${blockId}.${listViewIndex}.${listViewComponentId}`
                const existingErrors = listViewErrors.get(key) || new Set<string>()
                existingErrors.add(errorMessage)
                listViewErrors.set(key, existingErrors)
                return
            }
        }

        const errorMessage = error.message || translateErrorCode(error.code, block)
        const existingErrors = errorsByComponent.get(blockId) || new Set<string>()
        existingErrors.add(errorMessage)
        errorsByComponent.set(blockId, existingErrors)
    })

    // Apply list view errors
    listViewErrors.forEach((errorMessages, key) => {
        const [blockId, listViewIndex, listViewComponentId] = key.split('.')
        const block = flattenedBlocks.value.find(block => block.id === blockId)

        if (block && isListView(block)) {
            const item = block.items.find(item => String(item.index) === listViewIndex)
            const component = item?.components.find(component => component.id === listViewComponentId)
            if (component) {
                component.error = Array.from(errorMessages).join(', ')
            }
        }
    })

    // Apply regular block errors
    errorsByComponent.forEach((errorMessages, blockId) => {
        const block = flattenedBlocks.value.find(block => block.id === blockId)
        if (block) {
            updateBlock({
                ...block,
                error: Array.from(errorMessages).join(', '),
            })
        }
    })

    return handled
}
